<template lang="pug"> 
  .wave(:ref="`${collectionSlug}Wave`")
    canvas.wave__canvas(:ref="`${collectionSlug}Canvas`", :id="`${collectionSlug}-canvas`")
</template>

<script>
export default {
  name: "SectionWave",
  props: {
    color: {
      type: String,
      required: true
    },
    collectionSlug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      density: 0.95,
      friction: 0.9,
      areaOfEffect: 100,
      mousePullSpeed: 0.162,
      waves: null,
      canvas: 0,
      context: 0,
      idleTime: 0,
      setTimeUpdate: null,
      isMouseDown: false,
      mousePos: { x: 0, y: 0 },
      mouseSpeed: { x: 0, y: 0 },
      oldMousePos: { x: 0, y: 0 },
      oldScrollPosition: 0,
      forceSpeed: null
    };
  },
  computed: {
    width() {
      return window.innerWidth;
    },
    height() {
      return window.innerWidth >= 1200 ? 300 : 200;
    },
    waveDetail() {
      return Math.round(this.width / 40);
    },
    waveColor() {
      return this.color;
    }
  },
  mounted() {
    this.$nextTick(() => {
      var waveSelf = this;

      (function() {
        var lastTime = 0;
        var vendors = ["ms", "moz", "webkit", "o"];
        for (
          var x = 0;
          x < vendors.length && !window.requestAnimationFrame;
          ++x
        ) {
          window.requestAnimationFrame =
            window[vendors[x] + "RequestAnimationFrame"];
          window.cancelAnimationFrame =
            window[vendors[x] + "CancelAnimationFrame"] ||
            window[vendors[x] + "CancelRequestAnimationFrame"];
        }

        if (!window.requestAnimationFrame)
          window.requestAnimationFrame = function(callback, element) {
            var currTime = new Date().getTime();
            var timeToCall = Math.max(0, 16 - (currTime - lastTime));
            var id = window.setTimeout(function() {
              callback(currTime + timeToCall);
            }, timeToCall);
            lastTime = currTime + timeToCall;
            return id;
          };

        if (!window.cancelAnimationFrame)
          window.cancelAnimationFrame = function(id) {
            clearTimeout(id);
          };
      })();

      function Wave() {
        this.Init = function(canvasID) {
          waveSelf.canvas = document.getElementById(canvasID);
          waveSelf.canvas.width = waveSelf.width;
          waveSelf.canvas.height = waveSelf.height;
          waveSelf.context = waveSelf.canvas.getContext("2d");
          waveSelf.waves = [];

          for (let i = 0; i < waveSelf.waveDetail; i++) {
            waveSelf.waves.push({
              x: (waveSelf.width / (waveSelf.waveDetail - 4)) * (i - 2),
              y: waveSelf.height * 0.2,
              original: { x: 0, y: waveSelf.height * 0.5 },
              velocity: { x: 0, y: Math.round(Math.random() * 3) + 5 },
              force: { x: 0, y: 0 },
              mass: 10
            });
          }

          this.draw();
          this.ResizeCanvas();

          var thisWaveInstant = this;
          window.addEventListener(
            "scroll",
            function() {
              let newScrollPosition = this.pageYOffset;
              let scrollSpeed = Math.abs(
                newScrollPosition / waveSelf.oldScrollPosition
              );

              waveSelf.forceSpeed =
                scrollSpeed > 0 && scrollSpeed <= 2 ? scrollSpeed : 1;
              thisWaveInstant.InjectImpulseByScroll();
              waveSelf.oldScrollPosition = newScrollPosition;
            },
            false
          );

          waveSelf.canvas.addEventListener("mousemove", MouseMove, false);
          waveSelf.canvas.addEventListener(
            "mousedown",
            function(e) {
              var pos = { x: 0, y: 0 };
              waveSelf.isMouseDown = true;
              var mouseCoordinate = getMousePosition(e);
            },
            false
          );
          waveSelf.canvas.addEventListener(
            "mouseup",
            function(e) {
              waveSelf.isMouseDown = false;
            },
            false
          );
        };

        this.draw = function() {
          var self = this;
          this.UpdateCanvas();
          waveSelf.setTimeUpdate = window.requestAnimationFrame(function() {
            self.draw();
          });
        };

        this.UpdateCanvas = function(e) {
          waveSelf.context.clearRect(0, 0, waveSelf.width, waveSelf.height);
          waveSelf.context.fillStyle = waveSelf.waveColor;
          waveSelf.context.beginPath();
          waveSelf.context.moveTo(waveSelf.waves[0].x, waveSelf.waves[0].y);

          var i = 0;
          var length = waveSelf.waves.length;
          var previous, current, next;
          for (; i < length; i++) {
            previous = waveSelf.waves[i - 1];
            current = waveSelf.waves[i];
            next = waveSelf.waves[i + 1];

            if (previous != null && next != null) {
              var force = 0;
              force -= waveSelf.density * (previous.y - current.y);
              force += waveSelf.density * (current.y - next.y);
              force +=
                (waveSelf.density / 15) * (current.y - current.original.y);

              current.velocity.y -= force / previous.mass + current.force.y;
              current.force.y *= waveSelf.friction;
              current.velocity.y *= waveSelf.friction;
              current.y += current.velocity.y;

              var distanceFromMouse = GetDistanceBetween(
                current,
                waveSelf.mousePos
              );
              if (distanceFromMouse < waveSelf.areaOfEffect) {
                var dist = GetDistanceBetween(
                  { x: current.original.x, y: current.original.y },
                  waveSelf.mousePos
                );
                waveSelf.mouseSpeed.x *= 0.95;
                waveSelf.mouseSpeed.y *= 0.95;

                current.force.y +=
                  waveSelf.mousePullSpeed *
                  (1 - dist / waveSelf.areaOfEffect) *
                  waveSelf.mouseSpeed.y *
                  0.4;
              }
              waveSelf.context.quadraticCurveTo(
                previous.x,
                previous.y,
                previous.x + (current.x - previous.x) / 2,
                previous.y + (current.y - previous.y) / 2
              );
            }
          }

          waveSelf.context.lineTo(
            waveSelf.waves[waveSelf.waves.length - 1].x,
            waveSelf.waves[waveSelf.waves.length - 1].y
          );
          waveSelf.context.lineTo(waveSelf.width, waveSelf.height);
          waveSelf.context.lineTo(0, waveSelf.height);
          waveSelf.context.lineTo(waveSelf.waves[0].x, waveSelf.waves[0].y);

          waveSelf.context.fill();
          waveSelf.idleTime++;
        };

        this.InjectImpulseByScroll = function(e) {
          if (waveSelf.idleTime > 7) {
            waveSelf.canvas.removeEventListener("mousemove", this, false);
            var force = window.innerWidth >= 1200 ? 1 : 0.3;
            var random = Math.random();
            RandomWave(
              waveSelf.width * random,
              random * (force / waveSelf.forceSpeed + force)
            );
          }
        };

        this.ResizeCanvas = function(e) {
          waveSelf.canvas.width = waveSelf.width;
          waveSelf.canvas.height = waveSelf.height;

          for (var i = 0; i < waveSelf.waveDetail; i++) {
            (waveSelf.waves[i].x =
              (waveSelf.width / (waveSelf.waveDetail - 4)) * (i - 2)),
              (waveSelf.waves[i].y = waveSelf.height * 0.5);

            waveSelf.waves[i].original.x = waveSelf.waves[i].x;
            waveSelf.waves[i].original.y = waveSelf.waves[i].y;
          }
        };

        Array.prototype.contains = function(elt) {
          var i = this.length;
          while (i--) {
            if (this[i] === elt) return true;
          }
          return false;
        };

        function MouseMove(e) {
          waveSelf.idleTime = 0;

          waveSelf.mouseSpeed.x = Math.max(
            Math.min(waveSelf.mousePos.x - e.clientX, 40),
            -40
          );
          waveSelf.mouseSpeed.y = Math.max(
            Math.min(waveSelf.mousePos.y - e.clientY, 40),
            -40
          );

          waveSelf.mousePos.x = e.clientX;
          waveSelf.mousePos.y = e.clientY;
          waveSelf.oldMousePos.x = waveSelf.mousePos.x;
          waveSelf.oldMousePos.y = waveSelf.mousePos.y;
        }

        function RandomWave(position, force) {
          var wave =
            waveSelf.waves[
              Math.floor(
                (position / waveSelf.width) *
                  (Math.random() * waveSelf.waves.length)
              )
            ];

          if (wave) {
            wave.force.y += force;
            wave.force.y *= waveSelf.friction;
          }
        }

        function GetDistanceBetween(p1, p2) {
          var posX = p2.x - p1.x;
          var posY = p2.y - p1.y;
          return Math.sqrt(posX * posX + posY * posY);
        }

        Array.prototype.shuffle = function() {
          var i = this.length;
          var j, temp;
          while (--i) {
            j = Math.random() * (i + 1);
            temp = this[j];
            this[j] = this[i - 1];
            this[i - 1] = temp;
          }
        };

        function getMousePosition(e) {
          var x, y;
          var offset = {
            left: waveSelf.canvas.getBoundingClientRect().left,
            top: waveSelf.canvas.getBoundingClientRect().top
          };

          x =
            e.clientX +
            document.body.scrollLeft +
            document.documentElement.scrollLeft -
            offset.left;
          y =
            e.clientY +
            document.body.scrollTop +
            document.documentElement.scrollTop -
            offset.top;

          return {
            x: x,
            y: y
          };
        }

        HTMLCanvasElement.prototype.getMousePosition = getMousePosition;
      }

      var wave = new Wave();
      wave.Init(`${waveSelf.collectionSlug}-canvas`);

      window.addEventListener(
        "resize",
        function() {
          wave.ResizeCanvas();
        },
        false
      );
    });
  }
};
</script>

<style lang="scss">
@import "../styles/main.scss";

.wave {
  width: 100%;
  position: absolute;
  background: transparent;
  height: 200px;
  top: -195px;

  @include from("xl") {
    height: 300px;
    top: -295px;
  }
}
</style>
